/**
 * ClientReviewPanel - Bank Admin review component
 *
 * For Bank Admins to review reports, add comments, and provide additional information.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import {
  getReportWorkflow,
  addClientComment,
  addClientAdditionalInfo,
  completeClientReview,
  type ReportWorkflow,
  type ClientComment,
} from '../../../services/firestore';
import StatusBadge from '../../../components/reports/StatusBadge';
import DeadlineBadge from '../../../components/reports/DeadlineBadge';
import { useRatedInstitutions } from '../../../hooks/useInstitutions';
import '../ClientReview.css';

const REPORT_SECTIONS = [
  { id: 'executive_summary', title: 'Executive Summary' },
  { id: 'institution_overview', title: 'Institution Overview' },
  { id: 'capital_analysis', title: 'Capital Adequacy Analysis' },
  { id: 'asset_quality', title: 'Asset Quality Analysis' },
  { id: 'management_analysis', title: 'Management Analysis' },
  { id: 'earnings_analysis', title: 'Earnings Analysis' },
  { id: 'liquidity_analysis', title: 'Liquidity Analysis' },
  { id: 'sensitivity_analysis', title: 'Sensitivity to Market Risk' },
  { id: 'rating_rationale', title: 'Rating Rationale' },
  { id: 'outlook', title: 'Rating Outlook' },
];

interface ClientReviewPanelProps {
  report: ReportWorkflow;
  onUpdate: () => Promise<void>;
}

export default function ClientReviewPanel({ report: initialReport, onUpdate }: ClientReviewPanelProps) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { effectiveIsBankAdmin } = useRoleAssumption();

  const [report, setReport] = useState<ReportWorkflow>(initialReport);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>(REPORT_SECTIONS[0].id);

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [commentSection, setCommentSection] = useState<string | null>(null);
  const [showCommentForm, setShowCommentForm] = useState(false);

  // Additional info state
  const [additionalInfo, setAdditionalInfo] = useState(initialReport.client_additional_info || '');

  // Complete review confirmation
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  // Fetch institution data from BigQuery API
  const { institutions } = useRatedInstitutions();

  // Create a lookup map for institution names
  const institutionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    institutions.forEach((inst) => map.set(inst.institution_id, inst.institution_name));
    return map;
  }, [institutions]);

  const getInstitutionName = (institutionId: string): string => {
    return institutionNameMap.get(institutionId) || institutionId;
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleAddComment = async () => {
    if (!userProfile?.uid || !newComment.trim()) return;

    try {
      setProcessing(true);
      setError(null);

      await addClientComment(
        report.id,
        userProfile.uid,
        userProfile.display_name || userProfile.email,
        newComment,
        commentSection || undefined
      );

      // Refresh report
      const updatedReport = await getReportWorkflow(report.id);
      if (updatedReport) {
        setReport(updatedReport);
      }

      setNewComment('');
      setCommentSection(null);
      setShowCommentForm(false);
      setSuccessMessage('Comment added');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveAdditionalInfo = async () => {
    if (!userProfile?.uid) return;

    try {
      setProcessing(true);
      setError(null);

      await addClientAdditionalInfo(report.id, additionalInfo, userProfile.uid);

      setSuccessMessage('Additional information saved');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('Error saving additional info:', err);
      setError('Failed to save additional information');
    } finally {
      setProcessing(false);
    }
  };

  const handleCompleteReview = async () => {
    if (!userProfile?.uid) return;

    try {
      setProcessing(true);
      setError(null);

      await completeClientReview(report.id, userProfile.uid);

      setSuccessMessage('Review completed');
      setShowCompleteConfirm(false);
      setTimeout(() => navigate('/reports'), 1500);
    } catch (err) {
      console.error('Error completing review:', err);
      setError('Failed to complete review');
    } finally {
      setProcessing(false);
    }
  };

  const canReview = report.status === 'client_review' && effectiveIsBankAdmin;

  return (
    <div className="client-review-page">
      <div className="review-header">
        <button className="back-btn" onClick={() => navigate('/reports')}>
          ← Back
        </button>
        <div className="review-header-info">
          <h1>Review: {getInstitutionName(report.institution_id)}</h1>
          <div className="review-meta">
            <span className="period">{formatPeriod(report.period)}</span>
            <StatusBadge status={report.status} />
            <DeadlineBadge
              dueDate={report.deadline.final_due_date}
              isCompleted={report.status === 'approved'}
              size="medium"
            />
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="review-layout">
        {/* Left: Report Content */}
        <div className="report-content-area">
          {/* Section Navigation */}
          <div className="section-nav-horizontal">
            {REPORT_SECTIONS.map((section) => (
              <button
                key={section.id}
                className={`section-tab ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Content Viewer */}
          <div className="content-panel">
            <h2>{REPORT_SECTIONS.find((s) => s.id === activeSection)?.title}</h2>
            <div className="section-content">
              {report.content.sections[activeSection]?.content || (
                <span className="no-content">No content for this section</span>
              )}
            </div>

            {/* Add Comment Button */}
            {canReview && !showCommentForm && (
              <button
                className="add-section-comment-btn"
                onClick={() => {
                  setCommentSection(activeSection);
                  setShowCommentForm(true);
                }}
              >
                + Add Comment on This Section
              </button>
            )}
          </div>
        </div>

        {/* Right: Comments & Actions */}
        <div className="review-sidebar">
          {/* Comments Section */}
          <div className="comments-panel">
            <div className="panel-header">
              <h3>Comments ({report.client_comments.length})</h3>
              {canReview && (
                <button
                  className="add-comment-btn"
                  onClick={() => {
                    setCommentSection(null);
                    setShowCommentForm(true);
                  }}
                >
                  + Add
                </button>
              )}
            </div>

            {showCommentForm && (
              <div className="comment-form">
                <div className="comment-form-header">
                  {commentSection ? (
                    <span>
                      Comment on: {REPORT_SECTIONS.find((s) => s.id === commentSection)?.title}
                    </span>
                  ) : (
                    <span>General Comment</span>
                  )}
                </div>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment..."
                  rows={3}
                />
                <div className="comment-form-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowCommentForm(false);
                      setNewComment('');
                      setCommentSection(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="submit-comment-btn"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || processing}
                  >
                    Add Comment
                  </button>
                </div>
              </div>
            )}

            <div className="comments-list">
              {report.client_comments.length === 0 ? (
                <p className="no-comments">No comments yet</p>
              ) : (
                report.client_comments.map((comment: ClientComment) => (
                  <div
                    key={comment.id}
                    className={`comment-card ${comment.resolved ? 'resolved' : ''}`}
                  >
                    <div className="comment-meta">
                      <span className="comment-author">{comment.author_name}</span>
                      {comment.section_id && (
                        <span className="comment-on">
                          on {REPORT_SECTIONS.find((s) => s.id === comment.section_id)?.title}
                        </span>
                      )}
                    </div>
                    <p className="comment-text">{comment.content}</p>
                    {comment.resolved && (
                      <span className="resolved-tag">✓ Resolved</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Additional Information */}
          {canReview && (
            <div className="additional-info-panel">
              <h3>Additional Information</h3>
              <p className="panel-hint">
                Provide any additional context or information for the rating agency
              </p>
              <textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Enter any additional information here..."
                rows={4}
              />
              <button
                className="save-info-btn"
                onClick={handleSaveAdditionalInfo}
                disabled={processing}
              >
                Save Information
              </button>
            </div>
          )}

          {/* Complete Review */}
          {canReview && (
            <div className="complete-review-panel">
              <button
                className="complete-review-btn"
                onClick={() => setShowCompleteConfirm(true)}
                disabled={processing}
              >
                Complete Review
              </button>
              <p className="complete-hint">
                This will submit your review and send the report for committee approval
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Complete Confirmation Modal */}
      {showCompleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowCompleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Complete Review</h2>
            <p>
              Are you sure you want to complete your review? The report will be sent to
              the rating committee for final approval.
            </p>
            <p className="modal-stats">
              <strong>{report.client_comments.length}</strong> comment(s) added
              {additionalInfo && <>, <strong>1</strong> additional info provided</>}
            </p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowCompleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleCompleteReview}
                disabled={processing}
              >
                {processing ? 'Completing...' : 'Complete Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
